import { createSectionTitle, createSummaryTitle, formatDate, parseHtmlContent } from '../utils';

export const renderSocialHistory = (data, title) => {
  const content = [];
  const rows = [];

  content.push(createSectionTitle(title));

  // Handle noDetails case
  if (data && data.noDetails) {
    content.push({
      text: 'No details',
      style: 'narrative',
      margin: [0, 0, 0, 8]
    });
    return content;
  }

  // Helper function to check if an object has meaningful data
  const hasValidData = (obj) => {
    if (!obj) return false;
    return Object.values(obj).some((value) => {
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some((subValue) =>
          typeof subValue === 'string' ? subValue.trim() !== '' : Boolean(subValue)
        );
      }
      return Boolean(value);
    });
  };

  // Special validation for drugs to check for specific drug types
  const hasValidDrugData = (drugs) => {
    if (!drugs) return false;
    return (
      drugs.yes ||
      drugs.rarely ||
      drugs.never ||
      drugs.marijuana ||
      drugs.cocaine ||
      drugs.heroin ||
      drugs.methamphetamine ||
      drugs.prescription ||
      (drugs.other && drugs.other.trim() !== '') ||
      (drugs.timesMonth && drugs.timesMonth.trim() !== '')
    );
  };

  // Check if there's any meaningful data to display
  const hasData =
    data &&
    (hasValidData(data.cigarettes) ||
      hasValidData(data.vape) ||
      hasValidData(data.alcohol) ||
      hasValidDrugData(data.drugs) ||
      hasValidData(data.education) ||
      hasValidData(data.employed) ||
      hasValidData(data.exercise) ||
      hasValidData(data.martialStatus) ||
      hasValidData(data.sexualActivity) ||
      hasValidData(data.children));

  const hasNarrative = data && data.narrative && data.narrative.trim() !== '';

  // If no meaningful data and no narrative, show "No data"
  if (!hasData && !hasNarrative) {
    content.push({
      text: 'No data',
      style: 'narrative',
      margin: [0, 0, 0, 8]
    });
    return content;
  }

  // Helper function to create safe cell
  const createCell = (text, style = 'tableCell', colSpan = null) => {
    const cell = { text: text || '', style };
    if (colSpan) cell.colSpan = colSpan;
    return cell;
  };

  if (data.cigarettes && (data.cigarettes.yes || data.cigarettes.never || data.cigarettes.quit)) {
    const row = [createCell('Cigarettes:', 'tableHeader')];

    if (data.cigarettes.yes) {
      row.push(
        createCell('Yes'),
        createCell(`Packs day: ${data.cigarettes.packsDay || ''}`),
        createCell(`Years: ${data.cigarettes.yearsSmoked || ''}`)
      );
    } else if (data.cigarettes.never) {
      row.push(createCell('Never'), createCell(''), createCell(''));
    } else if (data.cigarettes.quit) {
      row.push(
        createCell('Quit'),
        createCell(`Date quit: ${formatDate(data.cigarettes.dateQuit) || ''}`, 'tableCell', 2)
      );
    }
    rows.push(row);
  }

  if (data.vape && (data.vape.yes || data.vape.no)) {
    rows.push([
      createCell('Vape:', 'tableHeader'),
      createCell(data.vape.yes ? 'Yes' : 'No'),
      createCell(''),
      createCell('')
    ]);
  }

  if (data.alcohol && (data.alcohol.yes || data.alcohol.never)) {
    const row = [createCell('Alcohol:', 'tableHeader')];

    if (data.alcohol.yes) {
      row.push(
        createCell('Yes'),
        createCell(`Drinks week: ${data.alcohol.drinksWeek || ''}`, 'tableCell', 2)
      );
    } else if (data.alcohol.never) {
      row.push(createCell('Never'), createCell(''), createCell(''));
    }
    rows.push(row);
  }

  if (data.drugs && (data.drugs.rarely || data.drugs.never || data.drugs.yes)) {
    const row = [createCell('Drugs:', 'tableHeader')];

    if (data.drugs.yes || data.drugs.rarely) {
      const frequency = data.drugs.yes ? 'Yes' : 'Rarely';
      const drugDetails = [];

      // Collect drug types
      if (data.drugs.marijuana) drugDetails.push('Marijuana');
      if (data.drugs.cocaine) drugDetails.push('Cocaine');
      if (data.drugs.heroin) drugDetails.push('Heroin');
      if (data.drugs.methamphetamine) drugDetails.push('Methamphetamine');
      if (data.drugs.prescription) drugDetails.push('Prescription drugs');
      if (data.drugs.other) drugDetails.push(`Other: ${data.drugs.other}`);

      if (drugDetails.length > 0) {
        // Show frequency, drug types, and times per month
        row.push(
          createCell(frequency),
          createCell(`Types: ${drugDetails.join(', ')}`),
          createCell(`Times month: ${data.drugs.timesMonth || ''}`)
        );
      } else {
        // Show frequency and times per month only
        row.push(
          createCell(frequency),
          createCell(`Times month: ${data.drugs.timesMonth || ''}`, 'tableCell', 2)
        );
      }
    } else if (data.drugs.never) {
      row.push(createCell('Never'), createCell(''), createCell(''));
    }
    rows.push(row);
  }

  if (data.education) {
    let educationText = '';
    if (data.education.highSchool) educationText = 'High school';
    else if (data.education.tradeSchool) educationText = 'Trade school';
    else if (data.education.college) educationText = 'College';
    else if (data.education.postGraduateDegree) educationText = 'Post graduate degree';

    if (educationText) {
      rows.push([
        createCell('Education:', 'tableHeader'),
        createCell(educationText, 'tableCell', 3)
      ]);
    }
  }

  if (data.employed) {
    const row = [createCell('Employed:', 'tableHeader')];

    if (data.employed.yes) {
      row.push(
        createCell('Yes'),
        createCell(`Type of work: ${data.employed.typeOfWork || ''}`, 'tableCell', 2)
      );
    } else if (data.employed.retired) {
      row.push(createCell('Retired', 'tableCell', 3));
    } else if (data.employed.no) {
      row.push(createCell('No', 'tableCell', 3));
    }
    rows.push(row);
  }

  if (data.exercise && (data.exercise.yes || data.exercise.no)) {
    const row = [createCell('Exercise:', 'tableHeader')];

    if (data.exercise.yes) {
      row.push(createCell('Yes'), createCell(`Type: ${data.exercise.type || ''}`, 'tableCell', 2));
      rows.push(row);

      // Add second row for activity and how often
      rows.push([
        createCell(''),
        createCell(`Activity: ${data.exercise.howLongActivity || ''}`),
        createCell(`How often: ${data.exercise.howOften || ''}`, 'tableCell', 2)
      ]);
    } else if (data.exercise.no) {
      row.push(createCell('No', 'tableCell', 3));
      rows.push(row);
    }
  }

  if (data.martialStatus) {
    let maritalText = '';
    if (data.martialStatus.married) maritalText = 'Married';
    else if (data.martialStatus.partnership) maritalText = 'Partnership';
    else if (data.martialStatus.divorced) maritalText = 'Divorced';
    else if (data.martialStatus.separated) maritalText = 'Separated';
    else if (data.martialStatus.single) maritalText = 'Single';
    else if (data.martialStatus.widow) maritalText = 'Widow';

    if (maritalText) {
      rows.push([
        createCell('Marital status:', 'tableHeader'),
        createCell(maritalText, 'tableCell', 3)
      ]);
    }
  }

  if (data.sexualActivity) {
    const row = [createCell('Sexual activity:', 'tableHeader')];

    if (data.sexualActivity.yes) {
      let orientation = '';
      if (data.sexualActivity.women) orientation = 'Women';
      else if (data.sexualActivity.men) orientation = 'Men';

      row.push(
        createCell('Yes'),
        createCell(
          `Number of sexual partners: ${data.sexualActivity.numberOfSexualPartners || ''}`
        ),
        createCell(orientation)
      );
      rows.push(row);

      if (data.sexualActivity.contracteption) {
        rows.push([
          createCell(''),
          createCell('Contraception: Yes'),
          createCell(
            `Method contraception: ${data.sexualActivity.methodContraception || ''}`,
            'tableCell',
            2
          )
        ]);
      }
    } else if (data.sexualActivity.no) {
      row.push(createCell('No', 'tableCell', 3));
      rows.push(row);
    }
  }

  if (data.children) {
    const row = [createCell('Children:', 'tableHeader')];

    if (data.children.yes) {
      row.push(
        createCell('Yes'),
        createCell(`Number of children: ${data.children.numberOfChildren || ''}`),
        createCell(`Ages: ${data.children.ages || ''}`)
      );
    } else if (data.children.no) {
      row.push(createCell('No', 'tableCell', 3));
    }
    rows.push(row);
  }

  // Show table only if there are rows
  if (rows.length > 0) {
    content.push({
      table: {
        widths: ['20%', '20%', '30%', '30%'],
        body: rows
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        paddingLeft: () => 0,
        paddingRight: () => 13,
        paddingTop: () => 8,
        paddingBottom: () => 8
      },
      margin: [0, 10, 0, 0]
    });
  }

  // Show narrative if it exists
  if (hasNarrative) {
    content.push(createSummaryTitle());
    content.push({
      text: parseHtmlContent(data.narrative),
      style: 'narrative',
      margin: [0, 0, 0, 8]
    });
  }

  // If no table rows and no narrative, show "No data" message
  if (rows.length === 0 && !hasNarrative) {
    content.push({
      text: 'No data',
      style: 'narrative',
      margin: [0, 0, 0, 8]
    });
  }

  return content;
};
